/* istanbul ignore file - better tested visually */

import { classNames } from '@partful/shared/lib/classNames';
import { ComponentProps, createContext, forwardRef, PropsWithChildren, useContext, useMemo, useState } from 'react';
import styles from './Tabs.module.scss';

type TabContextValue = {
  value?: string;
  onChange?: (value: string) => void;
};
const TabContext = createContext<TabContextValue>({});

type TabsProps = PropsWithChildren<{
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
}>;

/**
 * @example
 * // uncontrolled
 * <Tabs defaultValue='tab1'>
 *   <TabsList>
 *     <Tab value='tab1'>Tab 1</Tab>
 *     <Tab value='tab2'>Tab 2</Tab>
 *   </TabsList>
 *   <TabPanel value='tab1'>Tab 1 Content</TabPanel>
 *   <TabPanel value='tab2'>Tab 2 Content</TabPanel>
 * </Tabs>
 *
 * // controlled
 * <Tabs value={value} onChange={(newValue) => setValue(newValue)}>
 *   <TabsList>
 *     <Tab value='tab1'>Tab 1</Tab>
 *     <Tab value='tab2'>Tab 2</Tab>
 *   </TabsList>
 *   <TabPanel value='tab1'>Tab 1 Content</TabPanel>
 *   <TabPanel value='tab2'>Tab 2 Content</TabPanel>
 * </Tabs>
 */
export function Tabs({ defaultValue, value, onChange, children }: TabsProps) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);

  const contextValues = useMemo<TabContextValue>(
    () => ({ value: value ?? uncontrolledValue, onChange: onChange ?? ((newValue) => setUncontrolledValue(newValue)) }),
    [onChange, uncontrolledValue, value]
  );

  return <TabContext.Provider value={contextValues}>{children}</TabContext.Provider>;
}

export function useTabs() {
  return useContext(TabContext);
}

export const TabsList = forwardRef<HTMLDivElement, ComponentProps<'div'>>(({ className, children, ...rest }, ref) => (
  <div className={classNames(styles.tabsList, className)} {...rest} role="tablist" ref={ref}>
    {children}
  </div>
));

type TabProps = {
  value: string;
  disabled?: boolean;
} & ComponentProps<'button'>;
export const Tab = forwardRef<HTMLButtonElement, TabProps>(
  ({ value, onClick, disabled, className, children, ...rest }, ref) => {
    const { value: currentValue, onChange } = useTabs();

    return (
      <button
        disabled={disabled}
        className={classNames(styles.tab, className)}
        onClick={(e) => {
          onChange?.(value);
          onClick?.(e);
        }}
        id={`tab-${value}`}
        aria-controls={`tabpanel-${value}`}
        aria-selected={value === currentValue}
        {...rest}
        type="button"
        role="tab"
        ref={ref}
      >
        {children}
      </button>
    );
  }
);

type TabPanelProps = {
  value: string;
} & ComponentProps<'div'>;
export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(({ value, className, children, ...rest }, ref) => {
  const { value: currentValue } = useTabs();

  return (
    <div
      className={classNames(styles.tabPanel, className)}
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      {...rest}
      role="tabpanel"
      aria-hidden={value !== currentValue}
      hidden={value !== currentValue}
      ref={ref}
    >
      {children}
    </div>
  );
});
